import React, { useContext, useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import clsx from "clsx";
import { Link as RouterLink } from "react-router-dom";

import { CorpDomain } from "@safetyculture/s12-apis-connect-web/s12/identity/v1/org_domain_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { DEFAULT_REGION_CODE } from "../constants/index";
import { convertTimestampToDate, formatDateTimeDisplay } from "../helpers/time";
import mainStyles from "../styles";
import { RegionCode } from "../types/region";
import {
  addCorpDomain,
  deleteCorpDomain,
  listCorpDomains,
} from "api/endpoints/corp-domains";
import { callOptionsHeaders } from "api/helpers";
import CorpDomainDialog, {
  CorpDomainDialogMode,
} from "components/corp-domains/corp-domain-dialog";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";

const CORP_DOMAINS_PAGE_SIZE = 50;

interface DialogState {
  open: boolean;
  mode: CorpDomainDialogMode;
  domain?: string;
}

const CorpDomains = () => {
  const classes = mainStyles();
  const { hasPermissions } = useContext(PermissionsContext);
  const { showError } = useContext(SnackbarContext);

  const canCreate = hasPermissions([ActionType.CREATE_CORP_DOMAIN]);
  const canDelete = hasPermissions([ActionType.DELETE_CORP_DOMAIN]);

  const [regionCode, setRegionCode] = useState<RegionCode>(DEFAULT_REGION_CODE);
  const [domainFilter, setDomainFilter] = useState("");
  const [domains, setDomains] = useState<CorpDomain[]>([]);
  const [nextPageToken, setNextPageToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({
    open: false,
    mode: "add",
  });

  const hasLoadedRef = useRef(false);

  const shouldRenderSeeMoreBtn =
    !isLoading && domains.length !== 0 && nextPageToken;
  const shouldRenderCaughtUpMsg =
    !isLoading && domains.length !== 0 && !nextPageToken;

  const loadCorpDomains = (pageToken: string) => {
    hasLoadedRef.current = true;
    setIsLoading(true);
    listCorpDomains(
      {
        domainFilter,
        pageSize: CORP_DOMAINS_PAGE_SIZE,
        pageToken,
      },
      callOptionsHeaders(regionCode),
    )
      .then((res) => {
        const loadingFirstPage = pageToken === "";
        setDomains((prev) =>
          loadingFirstPage ? res.corpDomains : [...prev, ...res.corpDomains],
        );
        setNextPageToken(res.nextPageToken);
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  // Load the first page on mount and whenever the region changes.
  useEffect(() => {
    loadCorpDomains("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionCode]);

  const handleSearch = () => loadCorpDomains("");

  const clearSearch = () => {
    setDomainFilter("");
  };

  const openAddDialog = () => setDialog({ open: true, mode: "add" });
  const openDeleteDialog = (domain: string) =>
    setDialog({ open: true, mode: "delete", domain });
  const closeDialog = () => setDialog((prev) => ({ ...prev, open: false }));

  const handleDialogSubmit = async (domain: string) => {
    const headers = callOptionsHeaders(regionCode);
    if (dialog.mode === "add") {
      await addCorpDomain(domain, headers);
    } else {
      await deleteCorpDomain(domain, headers);
    }
    loadCorpDomains("");
  };

  const columnCount = canDelete ? 4 : 3;

  return (
    <Grid container sx={classes.basicSpacing} spacing={1}>
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <form
            className={clsx(classes.spaceSearch)}
            noValidate
            autoComplete="off"
            onSubmit={(event) => {
              event.preventDefault();
              handleSearch();
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="region"
                name="region-radio-group"
                value={regionCode}
                row
                onChange={(event) =>
                  setRegionCode(event.target.value as RegionCode)
                }
              >
                <FormControlLabel value="us" label="US" control={<Radio />} />
                <FormControlLabel value="au" label="AU" control={<Radio />} />
                <FormControlLabel value="eu" label="EU" control={<Radio />} />
              </RadioGroup>
            </FormControl>
            <TextField
              id="corp-domain-filter-input"
              label="Search domain"
              value={domainFilter}
              onChange={(event) => setDomainFilter(event.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ "data-testid": "corp-domain-filter-input" }}
              sx={{ ...classes.textInput, ...classes.basicSpacing }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              data-testid="corp-domain-search-btn"
              sx={classes.basicSpacing}
            >
              Search
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={clearSearch}
              data-testid="corp-domain-clear-btn"
              sx={classes.basicSpacing}
            >
              Clear
            </Button>
          </form>
          {canCreate && (
            <Button
              variant="contained"
              color="primary"
              onClick={openAddDialog}
              data-testid="corp-domain-add-btn"
              sx={classes.basicSpacing}
            >
              + Add domain
            </Button>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table aria-label="corp-domains">
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  <b>Domain</b>
                </TableCell>
                <TableCell align="left">
                  <b>Added by</b>
                </TableCell>
                <TableCell align="left">
                  <b>Added at</b>
                </TableCell>
                {canDelete && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && domains.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={columnCount}>
                    {hasLoadedRef.current
                      ? "No corp domains found."
                      : "Loading…"}
                  </TableCell>
                </TableRow>
              )}
              {domains.map((cd) => (
                <TableRow key={cd.domain} data-testid="corp-domain-row">
                  <TableCell align="left">
                    <Link
                      component={RouterLink}
                      to={`/search/user?emailDomain=${encodeURIComponent(
                        cd.domain,
                      )}`}
                      underline="hover"
                      data-testid="corp-domain-user-search-link"
                    >
                      {cd.domain}
                    </Link>
                  </TableCell>
                  <TableCell align="left">{cd.createdBy || "—"}</TableCell>
                  <TableCell align="left">
                    {cd.createdAt
                      ? formatDateTimeDisplay(
                          convertTimestampToDate(cd.createdAt),
                        )
                      : "—"}
                  </TableCell>
                  {canDelete && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(cd.domain)}
                        data-testid="corp-domain-delete-btn"
                      >
                        Delete
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={columnCount}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {!isLoading && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      ...classes.paddingTopSmall,
                      ...classes.paddingBottomSmall,
                    }}
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <Box>
                        {shouldRenderSeeMoreBtn && (
                          <Button
                            color="primary"
                            size="small"
                            onClick={() => loadCorpDomains(nextPageToken)}
                            data-testid="corp-domain-see-more-btn"
                          >
                            ...SEE MORE
                          </Button>
                        )}
                        {shouldRenderCaughtUpMsg && "You're all caught up"}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      <CorpDomainDialog
        mode={dialog.mode}
        open={dialog.open}
        domain={dialog.domain}
        onClose={closeDialog}
        onSubmit={handleDialogSubmit}
      />
    </Grid>
  );
};

export default CorpDomains;
